import { OutlinedSelect, SelectOptionType, addToast } from '@octano/global-ui';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useActionKitImp } from '../../../../components/ozmoit/common';
import { TranslationsKeys } from '../../../../locales/translations';
import { searchCourseGroupChildrenAction } from '../actions';
import { ChildSearchItem } from '../types';
import { ItemsFiltersValue } from './ItemsFilters';

interface ChildrenSearchSelectProps {
  groupId: number;
  filters: ItemsFiltersValue;
  excludeIds: Set<number>;
  onSelect: (item: ChildSearchItem) => void;
}

interface ChildSelectOption extends SelectOptionType {
  raw: ChildSearchItem;
}

/**
 * HU 15571 — Selector de actividades / sub-agrupaciones para agregarlas a la
 * Agrupación.
 *
 * Patrón pre-load (mismo que `views/Configurations/homologation/...`): cada vez
 * que el usuario aplica filtros (Tipo / Escuela / Plan de estudio) hacemos un
 * único fetch de `items_per_page=200` y pasamos las opciones estáticas al
 * `<OutlinedSelect>`. El componente de global-ui es `isSearchable` por default
 * así que el usuario puede tipear para filtrar DENTRO de la lista ya cargada;
 * abrir el dropdown muestra todos los resultados al toque sin esperar a que
 * tipee.
 *
 * `excludeIds` filtra en cliente los items que ya añadió localmente en la
 * sesión (defense-in-depth: el backend ya excluye los persistidos).
 *
 * Al elegir un ítem se llama `onSelect(item)` y se remonta el componente
 * (`key++`) para resetear la selección visualmente.
 */
const PRELOAD_PAGE_SIZE = 200;

export default function ChildrenSearchSelect({
  groupId,
  filters,
  excludeIds,
  onSelect,
}: ChildrenSearchSelectProps): ReactElement {
  const { t } = useTranslation(TranslationsKeys.COURSE_GROUPS);
  const [items, setItems] = useState<ChildSearchItem[]>([]);
  const [resetKey, setResetKey] = useState<number>(0);

  const { action: search, loading } = useActionKitImp(
    searchCourseGroupChildrenAction,
    {
      onSuccess: (resp) => {
        setItems(resp.items ?? []);
      },
      onError: {
        unknown: () => {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t('errors.generic'),
          });
          setItems([]);
        },
      },
    },
  );

  // Pre-cargamos las opciones cuando cambian los filtros aplicados (después de
  // que el usuario presiona Buscar) o cuando cambia el grupo en URL.
  useEffect(() => {
    if (!groupId || !filters.type) {
      return;
    }
    search(groupId, {
      type: filters.type,
      schoolId: filters.schoolId,
      studyPlanId: filters.studyPlanId,
      q: undefined,
      page: 0,
      items_per_page: PRELOAD_PAGE_SIZE,
    });
  }, [groupId, filters.type, filters.schoolId, filters.studyPlanId]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = useMemo<ChildSelectOption[]>(
    () =>
      items
        .filter((i) => !excludeIds.has(i.id))
        .map((i) => ({
          value: i.id,
          label: `${i.name} (${i.code})`,
          raw: i,
        })),
    [items, excludeIds],
  );

  const handleChange = (selected: ChildSelectOption | null): void => {
    if (!selected) {
      return;
    }
    onSelect(selected.raw);
    // Remontamos el select para resetear la selección visualmente.
    setResetKey((k) => k + 1);
  };

  return (
    <OutlinedSelect
      key={resetKey}
      name="childrenSearch"
      label={t('items.selector.label')}
      placeholder={t('items.selector.placeholder')}
      options={options}
      onChange={(opt) => handleChange(opt as ChildSelectOption | null)}
      isClearable
      disabled={loading}
    />
  );
}
